/**
 * TanStack Query hooks over `client.ts`.
 *
 * Pages never call the client directly — they call these. That gives one place
 * to set cache policy, and it is what makes the phase-7 offline story possible:
 * a persisted query cache plus stale-while-revalidate is where the "this feels
 * fast" payoff comes from, because Canvas itself is slow.
 *
 * Cache policy follows how fast the underlying data actually changes:
 *   - course list, people, syllabus: hours
 *   - assignments, modules, files:   minutes
 *   - submissions, grades, stream:   ~30s, because a new grade appearing late
 *                                    is the most annoying possible staleness
 */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryOptions,
  type UseQueryResult,
} from '@tanstack/react-query';

import * as client from './client';
import type {
  CanvasActivityItem,
  CanvasAssignment,
  CanvasAssignmentGroup,
  CanvasCalendarEvent,
  CanvasCourse,
  CanvasDiscussionEntry,
  CanvasDiscussionTopic,
  CanvasEnrollment,
  CanvasFile,
  CanvasId,
  CanvasModule,
  CanvasPage,
  CanvasPlannerItem,
  CanvasQuiz,
  CanvasSubmission,
  CanvasTodoItem,
  CanvasUser,
} from './types';
import {
  markModuleItemDone,
  postSubmissionComment,
  setPlannerOverride,
  submitFileUpload,
  submitText,
  submitUrl,
  type FileUploadInput,
} from './upload';
import { enqueue, isRetryable, type QueuedWrite } from '../writeQueue';

const SECOND = 1000;
const MINUTE = 60 * SECOND;

export const staleTimes = {
  /** Barely changes within a session. */
  static: 60 * MINUTE,
  /** Changes when a teacher edits something. */
  slow: 10 * MINUTE,
  /** Changes as work is assigned. */
  medium: 2 * MINUTE,
  /** Grades and submissions — refetch eagerly. */
  fast: 30 * SECOND,
} as const;

/**
 * Query keys, centralized.
 *
 * Every key starts with 'canvas' so the whole Canvas cache can be invalidated
 * in one call when the token changes — which must happen, or the previous
 * account's data would still be sitting in the cache.
 */
export const qk = {
  all: ['canvas'] as const,

  self: () => [...qk.all, 'self'] as const,
  colors: () => [...qk.all, 'colors'] as const,

  courses: (state: string) => [...qk.all, 'courses', state] as const,
  course: (id: CanvasId) => [...qk.all, 'course', id] as const,
  courseUsers: (id: CanvasId, type?: string) => [...qk.all, 'course', id, 'users', type ?? 'all'] as const,

  enrollments: () => [...qk.all, 'enrollments'] as const,

  assignmentGroups: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'assignment-groups'] as const,
  assignments: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'assignments'] as const,
  assignment: (courseId: CanvasId, id: CanvasId) => [...qk.all, 'course', courseId, 'assignment', id] as const,

  submission: (courseId: CanvasId, assignmentId: CanvasId) =>
    [...qk.all, 'course', courseId, 'submission', assignmentId] as const,
  courseSubmissions: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'submissions'] as const,

  modules: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'modules'] as const,
  files: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'files'] as const,
  pages: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'pages'] as const,
  page: (courseId: CanvasId, url: string) => [...qk.all, 'course', courseId, 'page', url] as const,

  discussions: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'discussions'] as const,
  discussion: (courseId: CanvasId, id: CanvasId) => [...qk.all, 'course', courseId, 'discussion', id] as const,
  discussionEntries: (courseId: CanvasId, id: CanvasId) =>
    [...qk.all, 'course', courseId, 'discussion', id, 'entries'] as const,
  announcements: (courseIds: CanvasId[]) => [...qk.all, 'announcements', [...courseIds].sort().join(',')] as const,

  quizzes: (courseId: CanvasId) => [...qk.all, 'course', courseId, 'quizzes'] as const,

  stream: () => [...qk.all, 'stream'] as const,
  todo: () => [...qk.all, 'todo'] as const,
  planner: (start?: string, end?: string) => [...qk.all, 'planner', start ?? '', end ?? ''] as const,
  calendar: (start: string, end: string, type: string) => [...qk.all, 'calendar', start, end, type] as const,

  health: () => [...qk.all, 'health'] as const,
} as const;

/** Options every query shares. Kept small on purpose. */
type Extra<T> = Omit<UseQueryOptions<T, Error, T, readonly unknown[]>, 'queryKey' | 'queryFn'>;

/* ------------------------------------------------------------------ */
/* Reads                                                               */
/* ------------------------------------------------------------------ */

export function useSelf(options?: Extra<CanvasUser>): UseQueryResult<CanvasUser, Error> {
  return useQuery({
    queryKey: qk.self(),
    queryFn: ({ signal }) => client.getSelf(signal),
    staleTime: staleTimes.static,
    ...options,
  });
}

export function useCourses(
  state: 'active' | 'completed' = 'active',
  options?: Extra<CanvasCourse[]>,
): UseQueryResult<CanvasCourse[], Error> {
  return useQuery({
    queryKey: qk.courses(state),
    queryFn: ({ signal }) => client.getCourses({ enrollmentState: state, signal }),
    staleTime: staleTimes.slow,
    ...options,
  });
}

export function useCourse(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasCourse>,
): UseQueryResult<CanvasCourse, Error> {
  return useQuery({
    queryKey: qk.course(courseId ?? ''),
    queryFn: ({ signal }) => client.getCourse(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.slow,
    ...options,
  });
}

export function useCourseUsers(
  courseId: CanvasId | undefined,
  enrollmentType?: 'student' | 'teacher',
  options?: Extra<CanvasUser[]>,
): UseQueryResult<CanvasUser[], Error> {
  return useQuery({
    queryKey: qk.courseUsers(courseId ?? '', enrollmentType),
    queryFn: ({ signal }) => client.getCourseUsers(courseId!, enrollmentType, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.slow,
    ...options,
  });
}

export function useSelfEnrollments(
  options?: Extra<CanvasEnrollment[]>,
): UseQueryResult<CanvasEnrollment[], Error> {
  return useQuery({
    queryKey: qk.enrollments(),
    queryFn: ({ signal }) => client.getSelfEnrollments(signal),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function useAssignmentGroups(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasAssignmentGroup[]>,
): UseQueryResult<CanvasAssignmentGroup[], Error> {
  return useQuery({
    queryKey: qk.assignmentGroups(courseId ?? ''),
    queryFn: ({ signal }) => client.getAssignmentGroups(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useAssignments(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasAssignment[]>,
): UseQueryResult<CanvasAssignment[], Error> {
  return useQuery({
    queryKey: qk.assignments(courseId ?? ''),
    queryFn: ({ signal }) => client.getAssignments(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useAssignment(
  courseId: CanvasId | undefined,
  assignmentId: CanvasId | undefined,
  options?: Extra<CanvasAssignment>,
): UseQueryResult<CanvasAssignment, Error> {
  return useQuery({
    queryKey: qk.assignment(courseId ?? '', assignmentId ?? ''),
    queryFn: ({ signal }) => client.getAssignment(courseId!, assignmentId!, signal),
    enabled: Boolean(courseId && assignmentId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useSubmission(
  courseId: CanvasId | undefined,
  assignmentId: CanvasId | undefined,
  options?: Extra<CanvasSubmission>,
): UseQueryResult<CanvasSubmission, Error> {
  return useQuery({
    queryKey: qk.submission(courseId ?? '', assignmentId ?? ''),
    queryFn: ({ signal }) => client.getSubmission(courseId!, assignmentId!, signal),
    enabled: Boolean(courseId && assignmentId),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function useCourseSubmissions(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasSubmission[]>,
): UseQueryResult<CanvasSubmission[], Error> {
  return useQuery({
    queryKey: qk.courseSubmissions(courseId ?? ''),
    queryFn: ({ signal }) => client.getCourseSubmissions(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function useModules(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasModule[]>,
): UseQueryResult<CanvasModule[], Error> {
  return useQuery({
    queryKey: qk.modules(courseId ?? ''),
    queryFn: ({ signal }) => client.getModules(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useFiles(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasFile[]>,
): UseQueryResult<CanvasFile[], Error> {
  return useQuery({
    queryKey: qk.files(courseId ?? ''),
    queryFn: ({ signal }) => client.getFiles(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function usePages(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasPage[]>,
): UseQueryResult<CanvasPage[], Error> {
  return useQuery({
    queryKey: qk.pages(courseId ?? ''),
    queryFn: ({ signal }) => client.getPages(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.slow,
    ...options,
  });
}

export function usePage(
  courseId: CanvasId | undefined,
  pageUrl: string | undefined,
  options?: Extra<CanvasPage>,
): UseQueryResult<CanvasPage, Error> {
  return useQuery({
    queryKey: qk.page(courseId ?? '', pageUrl ?? ''),
    queryFn: ({ signal }) => client.getPage(courseId!, pageUrl!, signal),
    enabled: Boolean(courseId && pageUrl),
    staleTime: staleTimes.slow,
    ...options,
  });
}

export function useDiscussions(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasDiscussionTopic[]>,
): UseQueryResult<CanvasDiscussionTopic[], Error> {
  return useQuery({
    queryKey: qk.discussions(courseId ?? ''),
    queryFn: ({ signal }) => client.getDiscussions(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useDiscussion(
  courseId: CanvasId | undefined,
  topicId: CanvasId | undefined,
  options?: Extra<CanvasDiscussionTopic>,
): UseQueryResult<CanvasDiscussionTopic, Error> {
  return useQuery({
    queryKey: qk.discussion(courseId ?? '', topicId ?? ''),
    queryFn: ({ signal }) => client.getDiscussion(courseId!, topicId!, signal),
    enabled: Boolean(courseId && topicId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useDiscussionEntries(
  courseId: CanvasId | undefined,
  topicId: CanvasId | undefined,
  options?: Extra<CanvasDiscussionEntry[]>,
): UseQueryResult<CanvasDiscussionEntry[], Error> {
  return useQuery({
    queryKey: qk.discussionEntries(courseId ?? '', topicId ?? ''),
    queryFn: ({ signal }) => client.getDiscussionEntries(courseId!, topicId!, signal),
    enabled: Boolean(courseId && topicId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useAnnouncements(
  courseIds: CanvasId[],
  options?: Extra<CanvasDiscussionTopic[]>,
): UseQueryResult<CanvasDiscussionTopic[], Error> {
  return useQuery({
    queryKey: qk.announcements(courseIds),
    queryFn: ({ signal }) => client.getAnnouncements(courseIds, signal),
    enabled: courseIds.length > 0,
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useQuizzes(
  courseId: CanvasId | undefined,
  options?: Extra<CanvasQuiz[]>,
): UseQueryResult<CanvasQuiz[], Error> {
  return useQuery({
    queryKey: qk.quizzes(courseId ?? ''),
    queryFn: ({ signal }) => client.getQuizzes(courseId!, signal),
    enabled: Boolean(courseId),
    staleTime: staleTimes.medium,
    ...options,
  });
}

export function useActivityStream(
  options?: Extra<CanvasActivityItem[]>,
): UseQueryResult<CanvasActivityItem[], Error> {
  return useQuery({
    queryKey: qk.stream(),
    queryFn: ({ signal }) => client.getActivityStream(signal),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function useTodo(options?: Extra<CanvasTodoItem[]>): UseQueryResult<CanvasTodoItem[], Error> {
  return useQuery({
    queryKey: qk.todo(),
    queryFn: ({ signal }) => client.getTodo(signal),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function usePlannerItems(
  range?: { startDate?: string; endDate?: string },
  options?: Extra<CanvasPlannerItem[]>,
): UseQueryResult<CanvasPlannerItem[], Error> {
  return useQuery({
    queryKey: qk.planner(range?.startDate, range?.endDate),
    queryFn: ({ signal }) => client.getPlannerItems({ ...range, signal }),
    staleTime: staleTimes.fast,
    ...options,
  });
}

export function useCalendarEvents(
  args: { startDate: string; endDate: string; contextCodes: string[]; type?: 'event' | 'assignment' },
  options?: Extra<CanvasCalendarEvent[]>,
): UseQueryResult<CanvasCalendarEvent[], Error> {
  return useQuery({
    queryKey: qk.calendar(args.startDate, args.endDate, args.type ?? 'event'),
    queryFn: ({ signal }) => client.getCalendarEvents({ ...args, signal }),
    enabled: args.contextCodes.length > 0,
    staleTime: staleTimes.medium,
    ...options,
  });
}

/* ------------------------------------------------------------------ */
/* Writes                                                              */
/* ------------------------------------------------------------------ */

/**
 * Every mutation below runs through `assertWriteAllowed` first (inside
 * client.ts), which throws unless the target is the mock. Nothing here can
 * reach a live gradebook while `allowLiveWrites` is false.
 *
 * OPTIMISTIC UI, WITH A ROLLBACK THAT MEANS IT (BUILD_PROMPT phase 6)
 * -------------------------------------------------------------------
 * Submitting paints the "Turned in" state immediately, because on school wifi
 * the round trip is slow enough that the alternative feels broken. But the
 * optimistic state is a *promise the app is making on Canvas's behalf*, and if
 * Canvas does not agree, that promise has to be visibly withdrawn:
 *
 *   onMutate   cancel in-flight refetches, snapshot the cache, paint the
 *              optimistic value
 *   onError    put the snapshot back — the UI must return to "not turned in",
 *              not linger on a submitted state that never happened
 *   onSettled  invalidate and refetch, because Canvas is the source of truth
 *              and the optimistic value was always a guess (brainstorm.md 1.1)
 *
 * A silently failed homework submission is the worst possible bug in this app,
 * so rollback is not a nicety. The pages pair it with a loud error state.
 *
 * Retryable failures are additionally handed to the offline write queue, so a
 * submission lost to a dropped connection is retried on reconnect rather than
 * simply lost (brainstorm.md 1.6).
 */

/** Paint a submission as though it had just been accepted. */
function optimisticSubmission(
  previous: CanvasSubmission | undefined,
  patch: Partial<CanvasSubmission>,
): CanvasSubmission {
  return {
    id: previous?.id ?? 'optimistic',
    assignment_id: previous?.assignment_id ?? '',
    user_id: previous?.user_id ?? 'self',
    ...previous,
    workflow_state: 'submitted',
    submitted_at: new Date().toISOString(),
    // A fresh submission is unmarked; leaving an old score visible next to
    // "Turned in" would read as though the new attempt had already been graded.
    score: null,
    grade: null,
    missing: false,
    ...patch,
  };
}

interface SubmissionContext {
  previous: CanvasSubmission | undefined;
  key: readonly unknown[];
}

async function beginOptimisticSubmission(
  queryClient: ReturnType<typeof useQueryClient>,
  vars: { courseId: CanvasId; assignmentId: CanvasId },
  patch: Partial<CanvasSubmission>,
): Promise<SubmissionContext> {
  const key = qk.submission(vars.courseId, vars.assignmentId);
  // Cancel first: an in-flight GET that resolves after the optimistic write
  // would overwrite it with stale data.
  await queryClient.cancelQueries({ queryKey: key });
  const previous = queryClient.getQueryData<CanvasSubmission>(key);
  queryClient.setQueryData<CanvasSubmission>(key, optimisticSubmission(previous, patch));
  return { previous, key };
}

function rollbackSubmission(
  queryClient: ReturnType<typeof useQueryClient>,
  context: SubmissionContext | undefined,
): void {
  if (!context) return;
  // `setQueryData(key, undefined)` would leave the cache holding undefined
  // rather than removing the entry, so an absent snapshot is removed explicitly.
  if (context.previous === undefined) queryClient.removeQueries({ queryKey: context.key });
  else queryClient.setQueryData(context.key, context.previous);
}

function invalidateAfterSubmission(
  queryClient: ReturnType<typeof useQueryClient>,
  vars: { courseId: CanvasId; assignmentId: CanvasId },
): void {
  void queryClient.invalidateQueries({ queryKey: qk.submission(vars.courseId, vars.assignmentId) });
  void queryClient.invalidateQueries({ queryKey: qk.courseSubmissions(vars.courseId) });
  void queryClient.invalidateQueries({ queryKey: qk.assignments(vars.courseId) });
  void queryClient.invalidateQueries({ queryKey: qk.assignmentGroups(vars.courseId) });
  void queryClient.invalidateQueries({ queryKey: qk.todo() });
  void queryClient.invalidateQueries({ queryKey: qk.planner() });
}

/** Hand a lost-to-the-network write to the retry queue; ignore anything final. */
function queueIfRetryable(error: unknown, write: QueuedWrite): void {
  if (!isRetryable(error)) return;
  void enqueue(write, error);
}

export interface SubmitTextVars {
  courseId: CanvasId;
  assignmentId: CanvasId;
  body: string;
  comment?: string;
}

export function useSubmitText(): UseMutationResult<
  CanvasSubmission,
  Error,
  SubmitTextVars,
  SubmissionContext
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SubmitTextVars) => submitText(vars),
    onMutate: (vars) =>
      beginOptimisticSubmission(queryClient, vars, {
        submission_type: 'online_text_entry',
        body: vars.body,
      }),
    onError: (error, vars, context) => {
      rollbackSubmission(queryClient, context);
      queueIfRetryable(error, {
        kind: 'submitText',
        courseId: vars.courseId,
        assignmentId: vars.assignmentId,
        body: vars.body,
        ...(vars.comment ? { comment: vars.comment } : {}),
      });
    },
    onSettled: (_data, _error, vars) => invalidateAfterSubmission(queryClient, vars),
  });
}

export interface SubmitUrlVars {
  courseId: CanvasId;
  assignmentId: CanvasId;
  url: string;
  comment?: string;
}

export function useSubmitUrl(): UseMutationResult<
  CanvasSubmission,
  Error,
  SubmitUrlVars,
  SubmissionContext
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: SubmitUrlVars) => submitUrl(vars),
    onMutate: (vars) =>
      beginOptimisticSubmission(queryClient, vars, {
        submission_type: 'online_url',
        url: vars.url,
      }),
    onError: (error, vars, context) => {
      rollbackSubmission(queryClient, context);
      queueIfRetryable(error, {
        kind: 'submitUrl',
        courseId: vars.courseId,
        assignmentId: vars.assignmentId,
        url: vars.url,
        ...(vars.comment ? { comment: vars.comment } : {}),
      });
    },
    onSettled: (_data, _error, vars) => invalidateAfterSubmission(queryClient, vars),
  });
}

export function useSubmitFile(): UseMutationResult<
  CanvasSubmission,
  Error,
  FileUploadInput,
  SubmissionContext
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: FileUploadInput) => submitFileUpload(vars),
    onMutate: (vars) =>
      beginOptimisticSubmission(queryClient, vars, { submission_type: 'online_upload' }),
    onError: (_error, _vars, context) => {
      // Deliberately NOT queued for retry: a File cannot be serialized into
      // IndexedDB and replayed later, and silently re-uploading a file the
      // student may since have edited is worse than telling them it failed.
      rollbackSubmission(queryClient, context);
    },
    onSettled: (_data, _error, vars) => invalidateAfterSubmission(queryClient, vars),
  });
}

export interface CommentVars {
  courseId: CanvasId;
  assignmentId: CanvasId;
  text: string;
}

export function usePostComment(): UseMutationResult<CanvasSubmission, Error, CommentVars, SubmissionContext> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: CommentVars) => postSubmissionComment(vars.courseId, vars.assignmentId, vars.text),
    onMutate: async (vars) => {
      const key = qk.submission(vars.courseId, vars.assignmentId);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<CanvasSubmission>(key);
      if (previous) {
        queryClient.setQueryData<CanvasSubmission>(key, {
          ...previous,
          submission_comments: [
            ...(previous.submission_comments ?? []),
            {
              id: `optimistic-${Date.now()}`,
              author_id: previous.user_id,
              author_name: 'You',
              comment: vars.text,
              created_at: new Date().toISOString(),
            },
          ],
        });
      }
      return { previous, key };
    },
    onError: (error, vars, context) => {
      rollbackSubmission(queryClient, context);
      queueIfRetryable(error, {
        kind: 'comment',
        courseId: vars.courseId,
        assignmentId: vars.assignmentId,
        text: vars.text,
      });
    },
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: qk.submission(vars.courseId, vars.assignmentId) });
    },
  });
}

export interface MarkDoneVars {
  courseId: CanvasId;
  moduleId: CanvasId;
  itemId: CanvasId;
  done: boolean;
}

export function useMarkModuleItemDone(): UseMutationResult<void, Error, MarkDoneVars> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: MarkDoneVars) =>
      markModuleItemDone(vars.courseId, vars.moduleId, vars.itemId, vars.done),
    onSettled: (_data, _error, vars) => {
      void queryClient.invalidateQueries({ queryKey: qk.modules(vars.courseId) });
    },
  });
}

export interface PlannerDoneVars {
  plannableType: string;
  plannableId: CanvasId;
  markedComplete: boolean;
}

export function useSetPlannerDone(): UseMutationResult<unknown, Error, PlannerDoneVars> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (vars: PlannerDoneVars) =>
      setPlannerOverride(vars.plannableType, vars.plannableId, vars.markedComplete),
    onError: (error, vars) => {
      queueIfRetryable(error, {
        kind: 'plannerDone',
        plannableType: vars.plannableType,
        plannableId: vars.plannableId,
        markedComplete: vars.markedComplete,
      });
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: qk.planner() });
      void queryClient.invalidateQueries({ queryKey: qk.todo() });
    },
  });
}
